import { Contexts } from 'react-vtk-js';
import type { Vector3 } from '@kitware/vtk.js/types';
import { add } from '@kitware/vtk.js/Common/Core/Math';
import { thickness as defaultThickness } from '../config';

import {
  useRef,
  useContext,
  useState,
  useEffect,
  useMemo,
  useLayoutEffect,
  useSyncExternalStore,
} from 'react';
import useLatest from 'react-use/esm/useLatest';
import useMountedState from 'react-use/esm/useMountedState';
import { rafThrottle } from 'utils/rafThrottle';
import { useViewportId } from '../modules/state';
import { encodeId } from '../utils/representationId';
import type { MouseHandlers } from '../types';
import { quickVec3Comparison, worldToDisplayOffset } from '../utils/math';
import { Polygon } from './Polygon';
import { DashedLine } from './DashedLine';
import { Line } from './Line';
import { withCommonPrimitiveHelpers } from './helpers/common';
import { getElementBox, useElementBox } from 'utils/useElementBox';
import { Circle, Expander } from '.';
import { CURSOR_NODE } from '../Annotations/helpers/cursors';
import { useOpenGLRenderWindow } from '../modules/useOpenGLRenderWindow';
import { useRenderer } from '../modules/useRenderer';
import { useAnnotationColors } from '../Annotations/helpers/useAnnotationColors';
import type { Bounds, IView } from 'react-vtk-js';
import {
  isSameRect,
  labelScaleForArea,
  leaderSegment,
  offsetRect,
  placeLabelAt,
  placeLabelInArea,
  projectBoundsToRect,
  sideForDirection,
  toPlacementArea,
} from '../utils/placeLabel';
import {
  getKeepOutRects,
  getLabelEntries,
  getLabelOffset,
  removeLabelEntry,
  removeLabelOffset,
  setLabelOffset,
  setLabelEntry,
  subscribeToLabelEntries,
} from '../utils/labelRegistry';
import type { Rect, StraightSide } from '../utils/placeLabel';
import {
  registerLabelBase,
  registerLabelClamp,
  setLabelWorldOffset,
  useLabelWorldOffset,
} from '../utils/labelMoveStore';
import { Editable } from './Editable';

// The link line is a faint dotted one, so it ties a label to its annotation without competing with it.
const LEADER_OPACITY = 0.6;
const LEADER_DASH_LENGTH = 2;

const HORIZONTAL_ALIGNMENT = {
  top: '0%',
  left: '-100%',
  center: '-50%',
  bottom: '100%',
  right: '0%',
} as const;
const VERTICAL_ALIGNMENT = {
  top: '0%',
  left: '-100%',
  center: '50%',
  bottom: '100%',
  right: '0%',
} as const;

const FLEX_ALIGNMENT = {
  top: 'flex-start',
  left: 'flex-start',
  center: 'center',
  bottom: 'flex-end',
  right: 'flex-end',
} as const;

// line-height (default of 20) / 2, action is a small square;
const ACTION_SIZE = 20;
const ACTION_PADDING = 2;

type DisplayRectProps = {
  cursor: string;
  id: string;
  rect: null | { bottom: number; left: number; right: number; top: number };
  z: number;
  visible?: boolean;
  opacity?: number;
  fill?: boolean;
  thickness?: number;
  color?: string;
};

const getWorldPoint = (x?: number, y?: number, z?: number, view?: IView | null): Vector3 | null => {
  const renderer = view?.getRenderer()?.get();
  const result =
    x != null && y != null && renderer != null
      ? view
          ?.getOpenGLRenderWindow()
          ?.get()
          .displayToWorld(x * window.devicePixelRatio, y * window.devicePixelRatio, z, renderer)
      : null;

  if (result == null) return null;

  return [result[0], result[1], result[2]] as Vector3;
};

/**
 * World offset from an annotation's anchor to the top left corner of its label, given the label's
 * rect in CSS px (y down) inside the placement area. Null when the point can't be mapped.
 */
const labelCornerWorldOffset = ({
  outer,
  areaHeight,
  areaOrigin,
  anchorWorld,
  z,
  view,
}: {
  outer: Rect;
  areaHeight: number;
  areaOrigin: { x: number; y: number };
  anchorWorld: Vector3;
  z?: number;
  view?: IView | null;
}): Vector3 | null => {
  const corner = getWorldPoint(
    outer.left + areaOrigin.x / window.devicePixelRatio,
    areaHeight - outer.top + areaOrigin.y / window.devicePixelRatio,
    z,
    view
  );

  return corner == null
    ? null
    : [corner[0] - anchorWorld[0], corner[1] - anchorWorld[1], corner[2] - anchorWorld[2]];
};

const DisplayRect = ({
  id,
  rect,
  z,
  cursor,
  visible,
  opacity = 0,
  fill = true,
  thickness = defaultThickness,
  color,
}: DisplayRectProps) => {
  const view = useContext(Contexts.ViewContext);

  const topLeft = getWorldPoint(rect?.left, rect?.top, z, view);
  const bottomLeft = getWorldPoint(rect?.left, rect?.bottom, z, view);
  const topRight = getWorldPoint(rect?.right, rect?.top, z, view);
  const bottomRight = getWorldPoint(rect?.right, rect?.bottom, z, view);
  if (topLeft == null || bottomLeft == null || topRight == null || bottomRight == null) return null;

  return (
    <Polygon
      id={id}
      fill={fill ? 'polys' : 'lines'}
      opacity={opacity}
      cursor={cursor}
      thickness={thickness}
      color={color}
      segments={[
        [topLeft, topRight],
        [topRight, bottomRight],
        [bottomRight, bottomLeft],
        [bottomLeft, topLeft],
      ]}
    />
  );
};

type LabelElementProps = {
  alignHorizontal: 'left' | 'right' | 'center' | 'top' | 'bottom';
  alignVertical: 'left' | 'right' | 'center' | 'top' | 'bottom';
  bottom?: number;
  children: React.ReactNode;
  color: string;
  opacity: number;
  cursor: string;
  id: string;
  left: number;
  right?: number;
  top: number;
  viewportId: string;
  z: number;
  visible?: boolean;
  orientWithinBoundingBox?: boolean;
  /** Display-space rect (device px, y up) of the annotation to keep the label beside, not on top of. */
  anchorRect?: Rect | null;
  /** Display-space rects (device px, y up) the label should stay off, e.g. the patient's tissue. */
  keepOutRects?: Rect[];
  /** Space in CSS px between the annotation and its label when auto placed. */
  placementGap?: number;
  /** The side auto placement tries first. */
  preferredSide?: StraightSide;
  /** Keep the label where it was first placed instead of re-placing it whenever things change. */
  stickyPlacement?: boolean;
  /**
   * The label belongs to a shape still being drawn. It is gone as soon as the shape is saved, so
   * the saved shape's label must not be placed around it: whether it was still there when the
   * saved label first placed itself (and which of the two yielded) decided where that label went.
   */
  isDrawing?: boolean;
  /**
   * Where the user put the label: its top left corner in device px from the bottom left of the
   * placement area. Wins over automatic placement.
   */
  manualCorner?: { x: number; y: number } | null;
  /** World point the label belongs to, and where the placement area starts on the canvas. */
  anchorWorld: Vector3;
  areaOrigin?: { x: number; y: number };
  /** Draw the link line solid, while the label or its annotation is hovered or being dragged. */
  isLeaderSolid?: boolean;
  /** Size in device px of the area the label must stay inside. */
  placementArea?: { width: number; height: number };
  borderPadding?: number;
  actionSize?: number;
  actionPadding?: number;
  showBorder?: boolean;
  ['data-testid']?: string;
  expandable: boolean;
  expanded: boolean;
  onExpandChanged?: (expanded: boolean) => void;
  editable?: boolean;
  onEdit?: () => void;
};

const LabelElement = ({
  children,
  id,
  viewportId,
  color,
  opacity,
  left,
  right,
  top,
  bottom,
  z,
  alignHorizontal,
  alignVertical,
  cursor,
  visible,
  orientWithinBoundingBox,
  anchorRect,
  keepOutRects,
  placementGap,
  preferredSide,
  stickyPlacement = false,
  isDrawing = false,
  manualCorner = null,
  anchorWorld,
  areaOrigin = { x: 0, y: 0 },
  isLeaderSolid = false,
  placementArea,
  borderPadding = 3,
  actionSize = ACTION_SIZE,
  actionPadding = ACTION_PADDING,
  showBorder = false,
  'data-testid': dataTestId,
  expandable,
  expanded: defaultExpanded,
  onExpandChanged,
  onEdit,
  editable = false,
}: LabelElementProps) => {
  const view = useContext(Contexts.ViewContext);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const canvasRect = useElementBox(view?.getOpenGLRenderWindow()?.get().getCanvas());
  const labelRef = useRef(null);
  const [rect, setRect] = useState<{
    left: number;
    top: number;
    right: number;
    bottom: number;
  } | null>(null);
  // Align the label off enough with border to not overlap annotation
  const borderOffset = showBorder ? borderPadding + 1 : 0;
  const computedLeft = left - borderOffset;

  // The annotation's rect in the same space the label is placed in (CSS px, y down).
  const placementSpace = useMemo(
    () =>
      anchorRect == null || placementArea == null || orientWithinBoundingBox
        ? null
        : toPlacementArea({
            anchorRect,
            keepOutRects,
            areaSize: placementArea,
            devicePixelRatio: window.devicePixelRatio,
          }),
    [anchorRect, keepOutRects, placementArea, orientWithinBoundingBox]
  );

  // Small viewports (tiles) get a smaller label, never a bigger one than normal.
  const placementScale = placementSpace == null ? 1 : labelScaleForArea(placementSpace);

  // Labels in the same viewport share where they are, so they can stay out of each other's way.
  const siblings = useSyncExternalStore(subscribeToLabelEntries, () => getLabelEntries(viewportId));

  // Auto placement: once the label is measured, pick the side of the annotation that keeps it
  // inside the viewport and clear of the other annotations and labels. The measured size doesn't
  // depend on where the label is, so this is stable.
  const keepOut = useMemo(() => getKeepOutRects(siblings, id), [siblings, id]);
  const fresh = useMemo(
    () =>
      placementSpace == null || rect == null
        ? null
        : placeLabelInArea({
            area: placementSpace,
            contentSize: { width: rect.right - rect.left, height: rect.top - rect.bottom },
            borderOffset,
            keepOut,
            gap: placementGap,
            preferredSide,
          }),
    [placementSpace, rect, borderOffset, keepOut, placementGap, preferredSide]
  );

  const contentSize = useMemo(
    () => (rect == null ? null : { width: rect.right - rect.left, height: rect.top - rect.bottom }),
    [rect]
  );
  // A manually placed label, or a sticky one once it has been placed, sits at a fixed world offset
  // from its annotation, so panning and zooming carry it along instead of re-placing it.
  const placed = useMemo(() => {
    if (placementSpace == null || contentSize == null) return null;
    if (manualCorner == null) return fresh;

    return placeLabelAt({
      area: placementSpace,
      contentSize,
      borderOffset,
      left: manualCorner.x / window.devicePixelRatio,
      top: placementSpace.areaHeight - manualCorner.y / window.devicePixelRatio,
    });
  }, [manualCorner, placementSpace, contentSize, borderOffset, fresh]);

  // The first automatic placement of a sticky label is remembered as a world offset, which the
  // parent turns into the manual corner from then on.
  useLayoutEffect(() => {
    if (!stickyPlacement || manualCorner != null) return;
    if (fresh == null || placementSpace == null) return;

    const offset = labelCornerWorldOffset({
      outer: fresh.outer,
      areaHeight: placementSpace.areaHeight,
      areaOrigin,
      anchorWorld,
      z,
      view,
    });
    if (offset == null) return;

    setLabelOffset(viewportId, id, offset);
  }, [
    stickyPlacement,
    manualCorner,
    anchorWorld,
    fresh,
    placementSpace,
    areaOrigin,
    z,
    view,
    viewportId,
    id,
  ]);

  const placedOuter = placed?.outer ?? null;
  const anchorCssRect = placementSpace?.anchor ?? null;
  useLayoutEffect(() => {
    // Auto placement can switch off (bounds or anchor go away, or the label is laid out in a
    // bounding box), so drop the entry or other labels keep avoiding a ghost. The label of a shape
    // being drawn is never an obstacle for the labels of saved shapes.
    if (anchorCssRect == null || isDrawing) {
      removeLabelEntry(viewportId, id);
      return;
    }
    setLabelEntry(viewportId, id, {
      anchor: anchorCssRect,
      label: placedOuter,
      settled: manualCorner != null,
    });
  }, [viewportId, id, anchorCssRect, placedOuter, manualCorner, isDrawing]);

  useLayoutEffect(() => () => removeLabelEntry(viewportId, id), [viewportId, id]);

  // Where the label's top left corner is in the world, for the canvas-relative CSS point given.
  const latestPlacedOuter = useLatest(placedOuter);
  const latestPlacementSpace = useLatest(placementSpace);
  const latestAreaOrigin = useLatest(areaOrigin);
  useEffect(() => {
    // A drag starts from wherever the label is now, however it got there.
    return registerLabelBase(viewportId, id, () => {
      const outer = latestPlacedOuter.current;
      const space = latestPlacementSpace.current;
      if (outer == null || space == null) return null;

      return labelCornerWorldOffset({
        outer,
        areaHeight: space.areaHeight,
        areaOrigin: latestAreaOrigin.current,
        anchorWorld,
        z,
        view,
      });
    });
  }, [
    viewportId,
    id,
    anchorWorld,
    z,
    view,
    latestPlacedOuter,
    latestPlacementSpace,
    latestAreaOrigin,
  ]);

  // A label being dragged is held inside the placement area: the offset is turned into the label's
  // screen corner, pulled back into the area, and turned back into an offset.
  const latestContentSize = useLatest(contentSize);
  useEffect(() => {
    return registerLabelClamp(viewportId, id, (offset) => {
      const space = latestPlacementSpace.current;
      const size = latestContentSize.current;
      const renderer = view?.getRenderer()?.get();
      const renderWindow = view?.getOpenGLRenderWindow()?.get();
      if (space == null || size == null || renderer == null || renderWindow == null) return offset;

      const ratio = window.devicePixelRatio;
      const origin = latestAreaOrigin.current;
      const corner = renderWindow.worldToDisplay(
        anchorWorld[0] + offset[0],
        anchorWorld[1] + offset[1],
        anchorWorld[2] + offset[2],
        renderer
      );
      const width = size.width + borderOffset * 2;
      const height = size.height + borderOffset * 2;
      const left = Math.min(
        Math.max((corner[0] - origin.x) / ratio, 0),
        Math.max(0, space.areaWidth - width)
      );
      const top = Math.min(
        Math.max(space.areaHeight - (corner[1] - origin.y) / ratio, 0),
        Math.max(0, space.areaHeight - height)
      );
      const world = renderWindow.displayToWorld(
        left * ratio + origin.x,
        (space.areaHeight - top) * ratio + origin.y,
        corner[2],
        renderer
      );

      const clamped: Vector3 = [
        world[0] - anchorWorld[0],
        world[1] - anchorWorld[1],
        world[2] - anchorWorld[2],
      ];
      // Never hand back a broken offset: it would make the label jump and re-render endlessly.
      return clamped.every(Number.isFinite) ? clamped : offset;
    });
  }, [
    viewportId,
    id,
    anchorWorld,
    view,
    borderOffset,
    latestContentSize,
    latestPlacementSpace,
    latestAreaOrigin,
  ]);

  // Once the label is apart from its annotation, a thin line keeps them tied together.
  const leaderEnds = (() => {
    if (placementSpace == null || placedOuter == null) return null;
    const leader = leaderSegment(placementSpace.anchor, placedOuter);
    if (leader == null) return null;

    const toWorld = (point: { x: number; y: number }) =>
      getWorldPoint(
        point.x + areaOrigin.x / window.devicePixelRatio,
        placementSpace.areaHeight - point.y + areaOrigin.y / window.devicePixelRatio,
        z,
        view
      );
    return { start: toWorld(leader.from), end: toWorld(leader.to) };
  })();
  const leaderStart = leaderEnds?.start ?? null;
  const leaderEnd = leaderEnds?.end ?? null;

  useLayoutEffect(() => {
    if (labelRef.current == null || canvasRect == null) return;

    const labelRect = getElementBox(labelRef.current);

    const r = {
      left: labelRect.left - canvasRect.left,
      top: canvasRect.height - (labelRect.top - canvasRect.top),
      right: labelRect.left - canvasRect.left + labelRect.width,
      bottom: canvasRect.height - (labelRect.top - canvasRect.top + labelRect.height),
    };

    setRect((rect) => {
      if (
        rect == null ||
        r.left !== rect.left ||
        r.top !== rect.top ||
        r.right !== rect.right ||
        r.bottom !== rect.bottom
      ) {
        return r;
      }

      return rect;
    });
  }, [canvasRect, left, bottom, children, placed?.left, placed?.bottom, placementScale]);

  // orientWithinBoundingBox decides how our styling will look
  // we have the ability to orient the label within the bounding box
  // or relative to an anchor point, using vertical and horizontal alignment
  // if orientWithinBoundingBox is true, we use flexbox to align the label within the bounding box
  // if orientWithinBoundingBox is false, we use transform to align the label relative to the anchor point

  const flexProps = `
    display: flex;
    align-items: ${FLEX_ALIGNMENT[alignVertical]};
    justify-content: ${FLEX_ALIGNMENT[alignHorizontal]};
    width: ${(right ?? 0) - left}px;
    height: ${(bottom ?? 0) - top}px;
    transform: translatey(100%);
  `;

  const justifyProps = `
    transform: translate(${HORIZONTAL_ALIGNMENT[alignHorizontal]}, ${VERTICAL_ALIGNMENT[alignVertical]});
  `;

  // A placed label is positioned by its own corner, so no alignment transform is needed.
  const placedProps = `
    transform: none;
  `;

  const expandedRect =
    rect != null
      ? {
          left: rect.left - borderPadding,
          right: rect.right + borderPadding,
          top: rect.top + borderPadding,
          bottom: rect.bottom - borderPadding,
        }
      : null;

  const topPoint = rect != null ? rect.top - actionSize / 2 : 0; // inline with center of text

  let leftPoint = rect != null ? rect.right - borderPadding - actionPadding : 0;

  const expanderVisible = showBorder && expandable && onExpandChanged != null;
  const editableVisible = showBorder && editable && onEdit != null;

  const expandedPoint = expanderVisible ? getWorldPoint(leftPoint, topPoint, z, view) : null;

  if (expanderVisible) {
    leftPoint -= actionSize;
  }

  const editablePoint = editableVisible ? getWorldPoint(leftPoint, topPoint, z, view) : null;

  let padding = 0;

  if (expanderVisible) {
    padding += actionSize + actionPadding;
  }

  if (editableVisible) {
    padding += actionSize;
  }

  return (
    <div
      id={encodeId({ viewportId }, id)}
      data-testid={dataTestId}
      role="note"
      css={`
        pointer-events: none;
        user-select: none;
        position: absolute;
        color: ${color};
        opacity: ${opacity};
        /* The following helps with contrast between the text and the background */
        text-shadow: 0px 0px 2px rgb(0 0 0 / 60%);
        white-space: nowrap;
        ${orientWithinBoundingBox === true
          ? flexProps
          : placed != null
            ? placedProps
            : justifyProps}
      `}
      style={{
        left: placed != null ? placed.left : computedLeft / window.devicePixelRatio,
        bottom: placed != null ? placed.bottom : (bottom ?? 0) / window.devicePixelRatio,
        ...(placed != null && placementScale < 1
          ? { transform: `scale(${placementScale})`, transformOrigin: 'left bottom' }
          : {}),
      }}
    >
      <div
        ref={labelRef}
        css={`
          > [data-element='label-header'] {
            padding-right: ${padding}px;
          }

          > [data-element='label-header']:has(+ [data-element='label-header']) {
            display: none;
          }
        `}
      >
        {(expandable || editable) && <LabelHeader>&nbsp;</LabelHeader>}
        {children}
        {(editableVisible || expanderVisible) && (
          <div style={{ position: 'absolute', top: 0, right: 0, pointerEvents: 'auto' }}>
            {editableVisible && editablePoint && onEdit && (
              <Editable
                id={id}
                onClick={() => {
                  onEdit();
                }}
              />
            )}
            {expanderVisible && expandedPoint && onExpandChanged && (
              <Expander
                id={id}
                expanded={expanded}
                onClick={() => {
                  setExpanded(!expanded);
                  onExpandChanged(!expanded);
                }}
              />
            )}
          </div>
        )}
      </div>

      {leaderStart != null && leaderEnd != null && isLeaderSolid && (
        <Line
          id={encodeId({ name: 'leader' }, id)}
          start={leaderStart}
          end={leaderEnd}
          color={color}
          opacity={opacity}
          thickness={1}
          pickable={false}
        />
      )}
      {leaderStart != null && leaderEnd != null && !isLeaderSolid && (
        <DashedLine
          id={encodeId({ name: 'leader' }, id)}
          start={leaderStart}
          end={leaderEnd}
          color={color}
          opacity={opacity * LEADER_OPACITY}
          dashLength={LEADER_DASH_LENGTH}
          thickness={1}
          pickable={false}
        />
      )}
      <DisplayRect id={id} rect={rect} z={z} cursor={cursor} />
      {showBorder && (
        <DisplayRect
          id={id}
          rect={expandedRect}
          z={z}
          cursor={cursor}
          fill={false}
          opacity={1}
          thickness={1}
          color={color}
        />
      )}
    </div>
  );
};

type LabelProps = {
  id: string;
  children: React.ReactNode;
  /** Label origin position */
  anchorPoint: Vector3;
  /** Text bounding box top left position */
  topLeftBoundingBoxPoint?: Vector3;
  /** Text bounding box bottom right position */
  bottomRightBoundingBoxPoint?: Vector3;
  /** Provide a vector to define the direction in which to offset the label */
  offsetDirection?: Vector3;
  /** Offset distance in pixels */
  offsetDistance?: number;
  color?: string;
  opacity?: number;
  alignVertical?: 'center' | 'top' | 'bottom';
  alignHorizontal?: 'left' | 'right' | 'center';
  cursor?: string;
  visible?: boolean;
  orientWithinBoundingBox?: boolean;
  ['data-testid']?: string;
  showAnchorPoint?: boolean;
  showBorder?: boolean;
  hoverProps?: Partial<MouseHandlers> & { color: string };
  expandable?: boolean;
  expanded?: boolean;
  onExpandChanged?: (expanded: boolean) => void;
  onEdit?: () => void;
  editable?: boolean;
  /**
   * World-space bounds of the annotation this label describes. When provided, the label is placed
   * on whichever side of these bounds keeps it inside the viewport, instead of always at the anchor.
   */
  placeAroundBounds?: Bounds;
  /** World-space areas auto placement should keep the label off, e.g. the patient's tissue. */
  keepOutBounds?: Bounds[];
  /** Space in CSS px between the annotation and its label when auto placed. Defaults to 8. */
  placementGap?: number;
  /**
   * World-space direction the label should be pushed toward from its annotation, e.g. out past the
   * end of a line. Auto placement tries the side this points to first.
   */
  placementDirection?: Vector3;
  /** Keep the label where it was first placed, instead of re-placing it as the view changes. */
  stickyPlacement?: boolean;
  /**
   * The label belongs to a shape still being drawn. It is gone as soon as the shape is saved, so
   * the saved shape's label must not be placed around it: whether it was still there when the
   * saved label first placed itself (and which of the two yielded) decided where that label went.
   */
  isDrawing?: boolean;
  /**
   * Where the user moved the label: world-space offset from `anchorPoint` to the label's top left
   * corner. When set, the label stays there (relative to the annotation) instead of being placed.
   */
  labelOffset?: Vector3;
} & MouseHandlers;

export const LabelHeader: React.ComponentType<{ children: React.ReactNode }> = ({ children }) => {
  return <div data-element="label-header">{children}</div>;
};

export const Label: React.ComponentType<LabelProps & MouseHandlers> = withCommonPrimitiveHelpers(
  ({
    id,
    children,
    anchorPoint,
    topLeftBoundingBoxPoint,
    bottomRightBoundingBoxPoint,
    offsetDirection = [0, 0, 0],
    offsetDistance = 0,
    color,
    opacity = 1,
    alignVertical = 'center',
    alignHorizontal = 'center',
    cursor = 'default',
    visible = true,
    orientWithinBoundingBox = false,
    showAnchorPoint = false,
    showBorder = false,
    hoverProps,
    expandable = false,
    expanded = false,
    onExpandChanged,
    onEdit,
    editable = false,
    placeAroundBounds,
    keepOutBounds,
    placementGap,
    placementDirection,
    stickyPlacement = false,
    isDrawing = false,
    labelOffset,
    ...rest
  }: LabelProps): React.ReactElement | null => {
    const view = useContext(Contexts.ViewContext);
    const openglRenderWindow = useOpenGLRenderWindow();
    const renderer = useRenderer();
    const interactor = view?.getRenderWindow()?.getInteractor();
    const viewportId = useViewportId();

    const [displayCoordinates, setDisplayCoordinates] = useState(
      openglRenderWindow && renderer
        ? add(
            openglRenderWindow.worldToDisplay(
              anchorPoint[0],
              anchorPoint[1],
              anchorPoint[2],
              renderer
            ),
            worldToDisplayOffset(offsetDirection, offsetDistance, openglRenderWindow, renderer),
            [0, 0, 0]
          )
        : null
    );

    // We destructure the array so that we can easily memoize the throttled
    // event callback below.
    // the bounding box is optional, we may just get a point to orient around
    const [pointX, pointY, pointZ] = topLeftBoundingBoxPoint ?? anchorPoint;
    const [bottomX, bottomY, bottomZ] = bottomRightBoundingBoxPoint ?? [0, 0, 0];

    const [boundingBoxCoordinates, setBoundingBoxCoordinates] = useState(
      openglRenderWindow && renderer
        ? add(
            openglRenderWindow.worldToDisplay(bottomX, bottomY, bottomZ, renderer),
            worldToDisplayOffset(offsetDirection, offsetDistance, openglRenderWindow, renderer),
            [0, 0, 0]
          )
        : null
    );
    const [anchorRect, setAnchorRect] = useState<Rect | null>(
      openglRenderWindow && renderer && placeAroundBounds
        ? projectBoundsToRect(placeAroundBounds, (x, y, z) =>
            openglRenderWindow.worldToDisplay(x, y, z, renderer)
          )
        : null
    );
    const latestAnchorRect = useLatest(anchorRect);
    const latestPlaceAroundBounds = useLatest(placeAroundBounds);
    const latestDisplayCoordinates = useLatest(displayCoordinates);
    const latestBoundingBoxCoordinates = useLatest(boundingBoxCoordinates);
    const isMounted = useMountedState();

    // This is throttled because the `onRenderEvent` callback is called once for each
    // rendered element, but we only want to update the display coordinates once per
    // render.
    // TODO(fzivolo): Replace this with a better solution once provided by kitware.
    // https://github.com/Kitware/react-vtk-js/issues/23#issuecomment-915103030
    const throttledRenderEventCallback = useMemo(
      () =>
        rafThrottle(() => {
          if (
            renderer == null ||
            openglRenderWindow == null ||
            !isMounted() ||
            !visible ||
            renderer.isDeleted()
          )
            return;

          const newDisplayCoordinates = add(
            openglRenderWindow.worldToDisplay(pointX, pointY, pointZ, renderer),
            worldToDisplayOffset(offsetDirection, offsetDistance, openglRenderWindow, renderer),
            [0, 0, 0]
          );

          const newBoundingBoxCoordinates = add(
            openglRenderWindow.worldToDisplay(bottomX, bottomY, bottomZ, renderer),
            worldToDisplayOffset(offsetDirection, offsetDistance, openglRenderWindow, renderer),
            [0, 0, 0]
          );

          const newAnchorRect =
            latestPlaceAroundBounds.current != null
              ? projectBoundsToRect(latestPlaceAroundBounds.current, (x, y, z) =>
                  openglRenderWindow.worldToDisplay(x, y, z, renderer)
                )
              : null;
          const anchorRectChanged = !isSameRect(newAnchorRect, latestAnchorRect.current);

          if (
            latestDisplayCoordinates.current == null ||
            !quickVec3Comparison(newDisplayCoordinates, latestDisplayCoordinates.current) ||
            anchorRectChanged
          ) {
            setDisplayCoordinates(newDisplayCoordinates);
            setBoundingBoxCoordinates(newBoundingBoxCoordinates);
            setAnchorRect(newAnchorRect);
          }
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [pointX, pointY, pointZ, offsetDirection, offsetDistance, renderer, openglRenderWindow]
    );

    // We use the useLatest hook to improve the overall performance
    // avoiding to update the `onRenderEvent` callback on each render
    const latestThrottledRenderEventCallback = useLatest(throttledRenderEventCallback);
    useEffect(
      () => {
        if (interactor == null) return;

        const subscription = interactor.onRenderEvent(() =>
          latestThrottledRenderEventCallback.current?.(undefined)
        );

        return () => {
          subscription.unsubscribe();
        };
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [interactor]
    );

    // A label being dragged right now uses the dragged position until the saved one catches up.
    const draggedOffset = useLabelWorldOffset(viewportId, id);
    const stickyOffset = useSyncExternalStore(subscribeToLabelEntries, () =>
      stickyPlacement ? getLabelOffset(viewportId, id) : undefined
    );
    const labelOffsetKey = labelOffset?.join(',');
    useEffect(() => {
      setLabelWorldOffset(viewportId, id, null);
    }, [viewportId, id, labelOffsetKey]);
    // Dragged to exactly where it is already saved, so the saved position is the one to show. The
    // effect above only runs when the saved position changes, which it did not.
    const draggedOffsetKey = draggedOffset?.join(',');
    useEffect(() => {
      if (draggedOffsetKey != null && draggedOffsetKey === labelOffsetKey) {
        setLabelWorldOffset(viewportId, id, null);
      }
    }, [viewportId, id, draggedOffsetKey, labelOffsetKey]);

    // Forget where the label was placed once its annotation is gone.
    useEffect(() => () => removeLabelOffset(viewportId, id), [viewportId, id]);

    const latestDisplayCoordinatesValue = latestDisplayCoordinates.current;
    const latestBoundingBoxCoordinatesValue = latestBoundingBoxCoordinates.current;

    const { defaultColor } = useAnnotationColors();

    if (
      displayCoordinates == null ||
      latestDisplayCoordinatesValue == null ||
      !visible ||
      latestBoundingBoxCoordinatesValue == null
    )
      return null;

    // displayCoordinates is currently (left, bottom) relative to the canvas, so
    // adjust to be relative to renderer.
    const size = openglRenderWindow?.getSize() ?? [0, 0];
    const viewport = renderer?.getViewport() ?? [0, 0];
    const [xmin, ymin] = Array.isArray(viewport) ? viewport : [0, 0];
    let [left, bottom, z] = latestDisplayCoordinatesValue;
    let [right, top] = latestBoundingBoxCoordinatesValue;
    left -= size[0] * xmin;
    bottom -= size[1] * ymin;
    top -= size[1] * ymin;
    right -= size[0] * xmin;

    const areaOrigin = { x: size[0] * xmin, y: size[1] * ymin };
    const effectiveOffset = draggedOffset ?? labelOffset ?? stickyOffset;
    let manualCorner: { x: number; y: number } | null = null;
    if (
      effectiveOffset != null &&
      placeAroundBounds != null &&
      openglRenderWindow != null &&
      renderer != null
    ) {
      const [cornerX, cornerY] = openglRenderWindow.worldToDisplay(
        anchorPoint[0] + effectiveOffset[0],
        anchorPoint[1] + effectiveOffset[1],
        anchorPoint[2] + effectiveOffset[2],
        renderer
      );
      manualCorner = { x: cornerX - areaOrigin.x, y: cornerY - areaOrigin.y };
    }

    let preferredSide: StraightSide | undefined;
    if (placementDirection != null && openglRenderWindow != null && renderer != null) {
      const [fromX, fromY] = openglRenderWindow.worldToDisplay(
        anchorPoint[0],
        anchorPoint[1],
        anchorPoint[2],
        renderer
      );
      const [toX, toY] = openglRenderWindow.worldToDisplay(
        anchorPoint[0] + placementDirection[0],
        anchorPoint[1] + placementDirection[1],
        anchorPoint[2] + placementDirection[2],
        renderer
      );
      preferredSide = sideForDirection(toX - fromX, toY - fromY);
    }

    const placementArea = Array.isArray(viewport)
      ? {
          width: size[0] * ((viewport[2] ?? 1) - xmin),
          height: size[1] * ((viewport[3] ?? 1) - ymin),
        }
      : undefined;

    return (
      <>
        {showAnchorPoint && (
          <Circle
            id={encodeId({ name: 'dot' }, id)}
            point={anchorPoint}
            radius={7}
            fill
            cursor={CURSOR_NODE}
            visible={visible}
            {...hoverProps}
          />
        )}
        <LabelElement
          id={id}
          data-testid={rest['data-testid']}
          viewportId={viewportId}
          color={color ?? defaultColor}
          isLeaderSolid={draggedOffset != null || (color != null && color !== defaultColor)}
          opacity={opacity}
          alignHorizontal={alignHorizontal}
          alignVertical={alignVertical}
          orientWithinBoundingBox={orientWithinBoundingBox}
          // anchorRect is state, so it outlives placeAroundBounds being removed.
          anchorRect={
            placeAroundBounds != null && anchorRect != null
              ? offsetRect(anchorRect, size[0] * xmin, size[1] * ymin)
              : null
          }
          keepOutRects={
            placeAroundBounds != null && openglRenderWindow != null && renderer != null
              ? keepOutBounds?.map((bounds) =>
                  offsetRect(
                    projectBoundsToRect(bounds, (x, y, z) =>
                      openglRenderWindow.worldToDisplay(x, y, z, renderer)
                    ),
                    size[0] * xmin,
                    size[1] * ymin
                  )
                )
              : undefined
          }
          placementGap={placementGap}
          preferredSide={preferredSide}
          stickyPlacement={stickyPlacement}
          isDrawing={isDrawing}
          manualCorner={manualCorner}
          anchorWorld={anchorPoint}
          areaOrigin={areaOrigin}
          placementArea={placementArea}
          left={left}
          bottom={bottom}
          right={right}
          top={top}
          z={z}
          cursor={cursor}
          showBorder={showBorder}
          expandable={expandable}
          expanded={expanded}
          editable={editable}
          onExpandChanged={onExpandChanged}
          onEdit={onEdit}
        >
          {children}
        </LabelElement>
      </>
    );
  }
);
